import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Crown, Swords, Coins, Sparkles, ArrowRight, Share2, RotateCcw } from "lucide-react";
import api from "../lib/api";
import RoasterAvatar from "../components/RoasterAvatar";
import { useRoastShare, ShareCardPortal } from "../hooks/useRoastShare";

// Free personality quizzes — fully client-side, no PII. Funnel back to the paid roast.
const QUIZZES = [
  {
    id: "roast_persona",
    title: "What's your roast persona?",
    emoji: "🔥",
    questions: [
      { q: "Someone roasts your outfit. You…", a: [["clap", "laugh hardest at yourself"], ["defend", "explain why it's art"], ["counter", "roast them back instantly"]] },
      { q: "Your group chat energy is…", a: [["clap", "the wholesome one"], ["defend", "the overthinker"], ["counter", "the menace"]] },
      { q: "Pick a weapon:", a: [["clap", "a hug"], ["defend", "a spreadsheet"], ["counter", "a flamethrower"]] },
    ],
    results: {
      clap: { title: "Good Sport", emoji: "😂", blurb: "You can take it and you love it. Certified fun to roast." },
      defend: { title: "The Overthinker", emoji: "🧠", blurb: "You'll survive the roast — right after you analyse it for 3 days." },
      counter: { title: "The Menace", emoji: "😈", blurb: "You don't get roasted. You roast back. Harder." },
    },
  },
  {
    id: "how_roastable",
    title: "How roastable are you?",
    emoji: "🌶️",
    questions: [
      { q: "Your dating bio is…", a: [["low", "painfully normal"], ["mid", "one bad pun"], ["high", "a red flag parade"]] },
      { q: "Last selfie you posted?", a: [["low", "well-lit and safe"], ["mid", "questionable angle"], ["high", "gym mirror, obviously"]] },
      { q: "Your LinkedIn headline has…", a: [["low", "a normal job title"], ["mid", "one buzzword"], ["high", "'ninja', 'guru' AND 'rockstar'"]] },
    ],
    results: {
      low: { title: "Hard Target", emoji: "🛡️", blurb: "Roasters will have to work for it. Respect." },
      mid: { title: "Fair Game", emoji: "🎯", blurb: "There's material here. Not a lot. But enough." },
      high: { title: "Free Real Estate", emoji: "🎪", blurb: "You are a roaster's dream. Submit before someone else does." },
    },
  },
];

function Quiz({ quiz, onDone }) {
  const [step, setStep] = useState(0);
  const [tally, setTally] = useState({});
  const pick = (key) => {
    const next = { ...tally, [key]: (tally[key] || 0) + 1 };
    setTally(next);
    if (step + 1 >= quiz.questions.length) {
      const winner = Object.entries(next).sort((a, b) => b[1] - a[1])[0][0];
      onDone(quiz, winner);
    } else setStep(step + 1);
  };
  const question = quiz.questions[step];
  return (
    <div data-testid={`quiz-active-${quiz.id}`}>
      <div className="text-xs text-zinc-500 uppercase tracking-widest mb-2">Question {step + 1} / {quiz.questions.length}</div>
      <div className="font-heading font-bold text-xl mb-4">{question.q}</div>
      <div className="grid gap-2">
        {question.a.map(([key, label], i) => (
          <button key={i} data-testid={`quiz-answer-${i}`} onClick={() => pick(key)}
            className="text-left px-4 py-3 rounded-sm border border-white/10 bg-zinc-950/50 hover:border-brand hover:bg-brand/10 transition-colors text-sm">
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Arena() {
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [result, setResult] = useState(null); // { quiz, r }
  const { share, busyId, shareRef, shareItem } = useRoastShare();

  useEffect(() => { api.get("/arena/leaderboard").then((r) => setBoard(r.data)).catch(() => {}); }, []);

  const finishQuiz = (quiz, key) => {
    const r = quiz.results[key];
    setActiveQuiz(null);
    setResult({ quiz, key, r });
    api.post("/arena/quiz-result", { quiz_id: quiz.id, result_key: key }).catch(() => {});
  };

  const shareQuiz = () => {
    share({ kind: "quiz", quiz_title: result.quiz.title, emoji: result.r.emoji,
      result_title: result.r.title, result_blurb: result.r.blurb }, "quiz");
  };

  const medal = (i) => (i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`);

  return (
    <div data-testid="arena-page">
      <div className="mb-8">
        <h1 className="font-heading font-bold uppercase tracking-tighter text-4xl sm:text-5xl flex items-center gap-3"><Trophy className="text-brand" /> Arena</h1>
        <p className="text-zinc-400 mt-2">Free social play — climb the leaderboard, take a quiz, then bring the heat in a real roast.</p>
      </div>

      {/* Leaderboard */}
      <div className="rounded-sm border border-white/10 bg-zinc-900/60 p-5 mb-8">
        <div className="flex items-center gap-2 mb-4"><Crown className="text-[#d4af37]" size={18} /><span className="text-sm font-bold tracking-widest uppercase text-[#d4af37]">Top Roasters</span></div>
        {!board ? (
          <div className="text-zinc-500 text-sm py-6 text-center">Loading…</div>
        ) : !board.active ? (
          <div data-testid="arena-leaderboard-coldstart" className="text-center py-8">
            <Sparkles className="text-brand mx-auto mb-3" size={26} />
            <div className="font-bold text-zinc-200">The leaderboard is just getting started</div>
            <p className="text-sm text-zinc-500 mt-1 max-w-md mx-auto">We're waiting on a bit more roasting activity before we crown the leaders. Be one of the first names up here — go win some roasts.</p>
            <button data-testid="arena-coldstart-cta" onClick={() => navigate("/app/queue")} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors">
              Start roasting <ArrowRight size={16} />
            </button>
          </div>
        ) : board.leaders.length === 0 ? (
          <div className="text-zinc-500 text-sm py-6 text-center">No ranked roasters yet.</div>
        ) : (
          <div className="space-y-2" data-testid="arena-leaderboard">
            {board.leaders.map((l, i) => (
              <button key={l.user_id} data-testid={`leader-${i}`} onClick={() => navigate(`/roaster/${l.user_id}`)}
                className="w-full flex items-center gap-3 rounded-sm border border-white/10 bg-zinc-950/40 px-4 py-3 hover:border-brand/40 transition-colors text-left">
                <span className="w-8 text-center font-heading font-bold text-lg">{medal(i)}</span>
                <RoasterAvatar avatar={l.avatar} initials={(l.handle || "R").replace("@", "").slice(0, 2).toUpperCase()} size={36} ring="#ff4500" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{l.handle}</div>
                  <div className="text-[11px] text-brand uppercase tracking-widest">{l.rank} roaster</div>
                </div>
                <div className="flex items-center gap-3 text-xs text-zinc-400">
                  <span className="flex items-center gap-1"><Trophy size={12} className="text-brand" /> {l.wins}</span>
                  {l.battle_wins > 0 && <span className="flex items-center gap-1"><Swords size={12} className="text-[#22d3ee]" /> {l.battle_wins}</span>}
                  {l.tips > 0 && <span className="flex items-center gap-1"><Coins size={12} className="text-[#d4af37]" /> ₹{l.tips.toFixed(0)}</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Free quizzes */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4"><Sparkles className="text-brand" size={18} /><span className="text-sm font-bold tracking-widest uppercase text-brand">Free quizzes</span></div>
        <div className="grid sm:grid-cols-2 gap-4">
          {QUIZZES.map((q) => (
            <div key={q.id} data-testid={`quiz-card-${q.id}`} className="rounded-sm border border-white/10 bg-zinc-900/60 p-5">
              {activeQuiz === q.id ? (
                <Quiz quiz={q} onDone={finishQuiz} />
              ) : (
                <>
                  <div className="text-3xl mb-2">{q.emoji}</div>
                  <div className="font-heading font-bold text-lg">{q.title}</div>
                  <div className="text-xs text-zinc-500 mt-1">{q.questions.length} quick questions · free</div>
                  <button data-testid={`quiz-start-${q.id}`} onClick={() => setActiveQuiz(q.id)}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand hover:bg-brand-hover text-black text-sm font-bold transition-colors">
                    Take the quiz <ArrowRight size={14} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Funnel back to paid */}
      <div className="rounded-sm border border-brand/30 bg-gradient-to-br from-brand/10 to-transparent p-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="font-heading font-bold text-xl">Had your fun? Now get roasted for real.</div>
          <div className="text-sm text-zinc-400 mt-1">Real humans, a flat fee, a certificate if you survive.</div>
        </div>
        <button data-testid="arena-funnel-cta" onClick={() => navigate("/app/submit")} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors">
          🔥 Get roasted <ArrowRight size={16} />
        </button>
      </div>

      {/* Quiz result */}
      {result && (
        <div data-testid="quiz-result" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setResult(null)}>
          <div className="w-full max-w-sm rounded-lg border border-brand/40 bg-zinc-950 p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="text-5xl mb-2">{result.r.emoji}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-widest">{result.quiz.title}</div>
            <div className="font-heading font-bold text-3xl uppercase mt-1">{result.r.title}</div>
            <p className="text-sm text-zinc-400 mt-2">{result.r.blurb}</p>
            <div className="flex flex-col gap-2 mt-5">
              <button data-testid="quiz-share-btn" onClick={shareQuiz} disabled={busyId === "quiz"}
                className="w-full py-3 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
                <Share2 size={16} /> {busyId === "quiz" ? "Preparing…" : "Share my result"}
              </button>
              <button data-testid="quiz-getroasted-btn" onClick={() => { setResult(null); navigate("/app/submit"); }}
                className="w-full py-2.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white font-bold transition-colors">
                🔥 Get roasted for real
              </button>
              <button onClick={() => { const q = result.quiz; setResult(null); setActiveQuiz(q.id); }} className="text-xs text-zinc-500 hover:text-white py-1 inline-flex items-center justify-center gap-1"><RotateCcw size={12} /> Retake</button>
            </div>
          </div>
        </div>
      )}
      <ShareCardPortal shareRef={shareRef} item={shareItem} />
    </div>
  );
}
